// Single allocation surface: rectangles sized by position value, coloured by
// P&L %, grouped by sector/currency/country via the toggle at top-right.
// Replaces AllocationDonuts (three breakdowns of position weights) +
// PositionHeatmap (position weights coloured by P&L) — both data slices were
// answering the same question.

import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import type { PositionWithTv } from '@/lib/api';
import { SectionCard } from './ui/SectionCard';
import { fmtMoney, fmtSignedPercent } from '@/lib/format';
import { cn } from '@/lib/cn';

type GroupBy = 'flat' | 'sector' | 'currency' | 'country';

type Props = {
  positions: PositionWithTv[];
};

type Leaf = {
  name: string;
  ticker: string;
  size: number;
  pnlPct: number;
  value: number;
  parent?: string;
};

const COUNTRY_FROM_EXCHANGE: Record<string, string> = {
  US: 'United States',
  L: 'United Kingdom',
  LSE: 'United Kingdom',
  XETRA: 'Germany',
  EU: 'Europe',
};

function color(pnlPct: number): string {
  const clamped = Math.max(-12, Math.min(12, pnlPct));
  if (clamped >= 0) {
    const t = clamped / 12;
    return `rgba(16, 185, 129, ${0.25 + t * 0.6})`;
  }
  const t = -clamped / 12;
  return `rgba(244, 63, 94, ${0.25 + t * 0.6})`;
}

function groupLabel(p: PositionWithTv, by: GroupBy): string {
  if (by === 'sector') return p.instrument.sector ?? 'Other';
  if (by === 'currency') return p.instrument.currency ?? 'Other';
  if (by === 'country') {
    const ex = p.instrument.exchange ?? '';
    return COUNTRY_FROM_EXCHANGE[ex] ?? ex ?? 'Other';
  }
  return '';
}

export function AllocationTreemap({ positions }: Props) {
  const navigate = useNavigate();
  const [by, setBy] = useState<GroupBy>('flat');

  const data = useMemo(() => {
    const leaves: Leaf[] = positions.map((p) => ({
      name: p.instrument.ticker.split('_')[0] ?? p.instrument.ticker,
      ticker: p.instrument.ticker,
      size: Math.max(0.1, p.walletImpact.currentValue),
      pnlPct:
        (p.walletImpact.unrealizedProfitLoss /
          Math.max(0.01, p.walletImpact.totalCost)) *
        100,
      value: p.walletImpact.currentValue,
      parent: by === 'flat' ? undefined : groupLabel(p, by),
    }));

    if (by === 'flat') return leaves;

    // Build a parent → children tree for recharts.
    const groups = new Map<string, Leaf[]>();
    for (const l of leaves) {
      const key = l.parent ?? 'Other';
      const arr = groups.get(key);
      if (arr) arr.push(l);
      else groups.set(key, [l]);
    }
    return Array.from(groups.entries()).map(([name, children]) => ({
      name,
      children,
    }));
  }, [positions, by]);

  return (
    <SectionCard>
      <div className="flex items-baseline justify-between mb-2 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-medium text-zinc-200">Allocation</h3>
          <p className="text-[11px] text-zinc-400">Size = value · Colour = P&amp;L %</p>
        </div>
        <div className="inline-flex rounded-md border border-zinc-800 overflow-hidden">
          {(['flat', 'sector', 'currency', 'country'] as GroupBy[]).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setBy(g)}
              aria-pressed={by === g}
              className={cn(
                'text-[11px] px-2 py-1 transition-colors capitalize',
                by === g
                  ? 'bg-indigo-500/15 text-indigo-100'
                  : 'text-zinc-400 hover:text-zinc-200',
              )}
            >
              {g}
            </button>
          ))}
        </div>
      </div>
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={data as never}
            dataKey="size"
            stroke="#0a0a0f"
            content={
              <HeatLeaf
                onClick={(ticker) =>
                  navigate({ to: '/positions/$ticker', params: { ticker } })
                }
              />
            }
            isAnimationActive={false}
          >
            <Tooltip
              cursor={{ stroke: '#3f3f46', strokeWidth: 1 }}
              contentStyle={{
                background: '#0a0a0f',
                border: '1px solid #26262f',
                borderRadius: 8,
                fontSize: 12,
              }}
              itemStyle={{ color: '#e4e4e7' }}
              formatter={(_v, _k, item) => {
                const p = (item.payload ?? {}) as Leaf;
                if (!p.ticker) return [p.name ?? '', ''];
                return [
                  `${fmtMoney(p.value)} (${fmtSignedPercent(p.pnlPct)})`,
                  p.name,
                ];
              }}
            />
          </Treemap>
        </ResponsiveContainer>
      </div>
    </SectionCard>
  );
}

type LeafProps = {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  // Recharts passes the leaf fields flattened, not as `payload`
  name?: string;
  ticker?: string;
  pnlPct?: number;
  onClick?: (ticker: string) => void;
  depth?: number;
  root?: unknown;
};

function HeatLeaf(props: LeafProps) {
  const { x = 0, y = 0, width = 0, height = 0, name, ticker, pnlPct = 0, onClick, depth = 0 } = props;
  if (width <= 0 || height <= 0) return null;
  // Parent rectangles (group nodes) are depth 1; leaves are depth 2 when grouped, depth 1 when flat.
  const isLeaf = typeof ticker === 'string';
  if (!isLeaf) {
    // Render only a faint outline + label for group containers
    const showGroupLabel = width > 40 && height > 18;
    return (
      <g>
        <rect x={x} y={y} width={width} height={height} fill="transparent" stroke="#3f3f46" strokeWidth={0.5} />
        {showGroupLabel && name && (
          <text
            x={x + 6}
            y={y + 12}
            fill="#a1a1aa"
            fontSize={10}
            fontWeight={600}
          >
            {name}
          </text>
        )}
      </g>
    );
  }
  const showLabel = width > 50 && height > 26;
  const fill = color(pnlPct);
  return (
    <g style={{ cursor: 'pointer' }} onClick={() => onClick?.(ticker)}>
      <rect x={x} y={y} width={width} height={height} fill={fill} stroke="#0a0a0f" />
      {showLabel && (
        <>
          <text
            x={x + 8}
            y={y + 16}
            fill="#fafafa"
            fontSize={12}
            fontWeight={600}
            fontFamily="JetBrains Mono, ui-monospace, monospace"
          >
            {name}
          </text>
          <text x={x + 8} y={y + 32} fill="#fafafa" fontSize={11}>
            {fmtSignedPercent(pnlPct)}
          </text>
        </>
      )}
    </g>
  );
}
